import { createAuth0Client } from '@auth0/auth0-spa-js';
import type { Auth0Client, Auth0ClientOptions, User } from '@auth0/auth0-spa-js';

let auth0ClientPromise: Promise<Auth0Client> | null = null;

function getRequiredEnvValue(key: string, value: string | undefined): string {
  if (!value) {
    throw new Error(`${key} is not configured.`);
  }
  return value;
}

function getAuth0Options(): Auth0ClientOptions {
  const domain = getRequiredEnvValue('VITE_AUTH0_DOMAIN', import.meta.env.VITE_AUTH0_DOMAIN);
  const clientId = getRequiredEnvValue(
    'VITE_AUTH0_CLIENT_ID',
    import.meta.env.VITE_AUTH0_CLIENT_ID
  );
  const redirectUri = getRequiredEnvValue(
    'VITE_AUTH0_REDIRECT_URI',
    import.meta.env.VITE_AUTH0_REDIRECT_URI
  );
  const audience = getRequiredEnvValue(
    'VITE_AUTH0_AUDIENCE',
    import.meta.env.VITE_AUTH0_AUDIENCE
  );

  return {
    domain,
    clientId,
    authorizationParams: {
      redirect_uri: redirectUri,
      audience,
      scope: 'openid profile email',
    },
    cacheLocation: 'localstorage',
    useRefreshTokens: false,
  };
}

export function getAuth0Client(): Promise<Auth0Client> {
  if (!auth0ClientPromise) {
    auth0ClientPromise = createAuth0Client(getAuth0Options());
  }
  return auth0ClientPromise;
}

export async function loginWithAuth0(): Promise<void> {
  const client = await getAuth0Client();
  await client.loginWithRedirect({
    appState: {
      returnTo: window.location.pathname,
    },
  });
}

export async function logoutAuth0(): Promise<void> {
  const client = await getAuth0Client();
  client.logout({
    logoutParams: {
      returnTo: window.location.origin,
    },
  });
  window.dispatchEvent(new CustomEvent('auth0-auth-change'));
}

export async function handleAuth0RedirectCallback() {
  const client = await getAuth0Client();
  const result = await client.handleRedirectCallback();
  window.dispatchEvent(new CustomEvent('auth0-auth-change'));
  return result;
}

export async function getAuth0AccessToken(): Promise<string> {
  const client = await getAuth0Client();
  const audience = getRequiredEnvValue(
    'VITE_AUTH0_AUDIENCE',
    import.meta.env.VITE_AUTH0_AUDIENCE
  );
  return client.getTokenSilently({
    authorizationParams: {
      audience,
    },
  });
}

export async function isAuth0Authenticated(): Promise<boolean> {
  const client = await getAuth0Client();
  return client.isAuthenticated();
}

export async function getAuth0User(): Promise<User | undefined> {
  const client = await getAuth0Client();
  return client.getUser();
}
